import React, { useState } from 'react';
import {
  IAuditoriaAnomaliaTarifaBancariaResultado,
  IItemAnomaliaTarifaBancaria,
} from '../interfaces/IAuditoriaAnomaliaTarifaBancaria';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaAnomaliaTarifaBancariaResultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

// COR-160: quando o alerta é por divergência de classificação contábil (IOF/Câmbio, Juros/
// Empréstimo/Financiamento), não existe grupo estatístico comparável — Média do Período e % da
// Média vêm zerados propositalmente no backend. Exibir "R$ 0,00"/"0%" nesse caso sugere
// (incorretamente) que o alerta veio de desproporcionalidade de valor; a tela deve deixar claro
// que o critério de detecção foi outro.
const renderMediaPeriodo = (item: IItemAnomaliaTarifaBancaria) =>
  item.ehAnomaliaHistorico
    ? <span className='text-muted' style={{ fontStyle: 'italic' }}>Não aplicável</span>
    : formatCurrency(item.mediaPeriodo);

const renderPercentualDesvio = (item: IItemAnomaliaTarifaBancaria) =>
  item.ehAnomaliaHistorico
    ? <span className='text-muted' style={{ fontStyle: 'italic' }}>Não aplicável</span>
    : `${item.percentualDesvio.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

const renderCriterioDeteccao = (item: IItemAnomaliaTarifaBancaria) =>
  item.ehAnomaliaHistorico
    ? `Classificação Contábil (${item.categoriaHistorico || 'divergência de conta'})`
    : 'Estatístico (valor desproporcional)';

const AuditoriaAnomaliaTarifaBancariaDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const anomaliasDetectadas = resultado.anomaliasDetectadas || [];
  const tarifasRegulares = resultado.tarifasRegulares || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Anomalias em Tarifas Bancárias — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFinanceiroTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Valor em Anomalias</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFinanceiroTotal)}</div>
          {resultado.impactoFinanceiroTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Auditoria de classificação contábil — sem multa/imposto calculado.</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>Anomalias detectadas e tarifas regulares no período.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base fiscal e contábil aplicada a esta auditoria.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
          <div className='mt-2'><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Anomalias Detectadas</h3>
        <p className='text-muted'>
          Lançamentos com valor desproporcional (mais de 5x a média do período) ou com indício de juros/empréstimo/
          financiamento lançado na conta de tarifas bancárias — erro de classificação contábil.
        </p>

        {anomaliasDetectadas.length === 0 && (
          <div className='text-muted mt-4'>Nenhuma anomalia detectada no período.</div>
        )}

        {anomaliasDetectadas.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Mês de Vencimento</th>
                  <th className='text-end'>Valor</th>
                  <th>Critério de Detecção</th>
                  <th className='text-end'>Média do Período</th>
                  <th className='text-end'>% da Média</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {anomaliasDetectadas.map((item: IItemAnomaliaTarifaBancaria, idx) => {
                  const chave = `at-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-alerta' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.mesVencimento}</td>
                        <td className='text-end'>{formatCurrency(item.valorLancamento)}</td>
                        <td style={{ fontSize: 12 }}>{renderCriterioDeteccao(item)}</td>
                        <td className='text-end'>{renderMediaPeriodo(item)}</td>
                        <td className='text-end'>{renderPercentualDesvio(item)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ background: '#fef2f2' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Critério de Detecção:</strong> {renderCriterioDeteccao(item)}.</div>
                              <div><strong>Lançamento (Histórico):</strong> {item.historico}.</div>
                              <div><strong>Conta Referencial:</strong> {item.contaReferencial} - {item.descricaoConta}.</div>
                              <div><strong>Data de Lançamento:</strong> {formatDate(item.dataLancamento)} (mês de vencimento: {item.mesVencimento}).</div>
                              <div><strong>Valor do Lançamento:</strong> {formatCurrency(item.valorLancamento)}.</div>
                              <div><strong>Média do Período:</strong> {renderMediaPeriodo(item)}.</div>
                              <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
                              <div><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
                              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>{item.memoriaCalculo}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {tarifasRegulares.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-info-circle-fill text-warning me-2' style={{ fontSize: 18 }} />
            Tarifas Bancárias Regulares (por Mês de Vencimento)
          </h3>
          <p className='text-muted'>
            Lançamentos de tarifa bancária sem anomalia — dentro do padrão esperado de valor e sem indício de
            classificação errada. Trazidos como aviso, com o mês de vencimento de cada tarifa.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Mês de Vencimento</th>
                  <th className='text-end'>Valor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {tarifasRegulares.map((item: IItemAnomaliaTarifaBancaria, idx) => {
                  const chave = `atr-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.mesVencimento}</td>
                        <td className='text-end'>{formatCurrency(item.valorLancamento)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={4} style={{ background: '#fffbeb' }}>
                            <div className='py-2 px-3'>
                              <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaAnomaliaTarifaBancariaDetalhe;
